import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FooterLink, QbItaSimpleCoverage, QbItaStep, getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import { CurrencyIsoCode, HomeProject, PaymentModality } from '@models/swagger';
import { select, Store } from '@ngrx/store';
import { IntegrationService } from '@services/integration/integration.service';
import { AppState } from '@store/app.state';
import { GenesysChatService } from '@core/services/genesys-chat/genesys-chat.service';
import { GenesysChat } from '@core/models/configurator';
import {
  GetIntegrationsRequest,
  integrationGetFooterAccessibilityUrl,
  integrationGetFooterCookiesUrl,
  integrationGetFooterPrivacyUrl,
  integrationGetGenesysChatConfig,
  integrationGetVertiCommercialPageUrl,
} from '@store/configurator/getIntegrations';
import {
  getPartnershipDocument,
  getPartnershipImage,
  getPartnershipName,
  getPartnershipUrl,
} from '@store/flow-data/configurator';
import { getGenesysChatInfo, getPartnershipIntegrationInd } from '@store/flow-data/general';
import { getProjectData } from '@store/flow-data/project';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { RISKHOME } from '@global/constants';
import { Flow } from '@global/routePaths';
import { ApplicationUtilitiesService } from '@core/services/api/http/applicationUtilitites.service';
import { TranslateService } from '@ngx-translate/core';
import { GenesysChatConfig } from '@core/models';
import { ChatConfigVisibilityService } from '@core/services/chat-config-visibility/chat-config-visibility.service';

@Component({
  selector: 'qb-ita-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() contentCentered = false;
  @Input() contentPadding = false;

  // Page spinner
  @Input() lockUserInteraction: boolean;

  // Content component info
  @Input() stepPageTitle?: string;
  @Input() stepPageDescription?: string;
  @Input() stepPageIcon?: string;

  // Header inputs
  @Input() headerPromotionalImage?: string;
  @Input() headerPromotionalUrl?: string;
  @Input() headerPromotionalShowInd = true;
  // Footer inputs
  @Input() footerCookiesUrl?: string;
  @Input() footerPrivacyUrl?: string;
  @Input() footerDocumentUrl?: string;
  // Steps inputs
  @Input() steps: QbItaStep[];
  @Input() showSteps = true;
  /**
   * Decimal format. First number is the main step, second number is substep
   * @example 1.1
   */
  @Input() currentStep: string;
  @Input() percent?: number;

  // Back button
  @Input() hideBackButton = false;
  @Input() backBtnTitle?: string;
  @Input() backBtnDescription?: string;
  @Output() backBtnClick: EventEmitter<void> = new EventEmitter();

  // Continue button
  @Input() hideNextButton = false;
  @Input() hideNextButtonMobile = false;
  @Input() nextBtnTitle?: string;
  @Input() nextBtnDisabled?: boolean;
  @Input() nextBtnPosition?: string;
  @Input() nextButtonShowSpinner?: boolean;
  @Output() nextBtnClick: EventEmitter<void> = new EventEmitter();

  // Summary lite
  @Input() showSummaryLite?: boolean;
  @Input() summaryLiteHeaderTitle?: string;
  @Input() summaryLiteHeaderSubtitle?: string;
  @Input() summaryLitePriceValue?: number;
  @Input() summaryLitePriceCurrencyIsoCode?: CurrencyIsoCode;
  @Input() summaryLiteSimpleCoverages: QbItaSimpleCoverage[];
  @Output() summaryLiteOnEditBtnClick: EventEmitter<void> = new EventEmitter();

  // WebChannelAlert
  @Input() showWebChannelAlert?: boolean;

  @Input() genesysChatVisibilityInd: boolean;
  @Input() flowName: Flow;
  @Input() stepName: string;

  vertiUrl$: Observable<string>;

  onDesktop: boolean;

  homeProject: HomeProject = {};
  homeTypeName: string;
  partnershipIntegrationInd$: Observable<boolean>;

  partnershipUrl$: Observable<string>;
  partnershipImage$: Observable<string>;
  promotionalAlt: string;

  genesysChatAvailable: boolean;

  genesysChatInfo: GenesysChat;
  genesysChatConfig: GenesysChatConfig;

  chatAvailable: boolean;
  visibleInd: boolean;

  footerLinks$: Observable<FooterLink[]>;

  chatConfigVisibility: any;

  get priceLabel(): string {
    let label = '';
    if (
      this.homeProject?.premiumPaymentModality?.paymentModalityTypeCode ===
      PaymentModality.PaymentModalityTypeCodeEnum.Everyyear
    ) {
      label = 'COVERAGES_LITE.PRICE.YEAR';
    } else if (
      this.homeProject?.premiumPaymentModality?.paymentModalityTypeCode ===
      PaymentModality.PaymentModalityTypeCodeEnum.Everysixmonths
    ) {
      label = 'COVERAGES_LITE.PRICE.SEMESTER';
    } else if (
      this.homeProject?.premiumPaymentModality?.paymentModalityTypeCode ===
      PaymentModality.PaymentModalityTypeCodeEnum.Quarterly
    ) {
      label = 'COVERAGES_LITE.PRICE.QUARTER';
    } else if (
      this.homeProject?.premiumPaymentModality?.paymentModalityTypeCode ===
      PaymentModality.PaymentModalityTypeCodeEnum.Monthly
    ) {
      label = 'COVERAGES_LITE.PRICE.MONTH';
    }
    return label;
  }

  constructor(
    private readonly store: Store<AppState>,
    private readonly integrationService: IntegrationService,
    private readonly genesysChatService: GenesysChatService,
    private readonly applicationUtilitiesService: ApplicationUtilitiesService,
    private readonly translateService: TranslateService,
    private readonly chatConfigVisibilityService: ChatConfigVisibilityService
  ) {
    this.footerLinks$ = combineLatest([
      // cookies
      this.store.pipe(select(integrationGetFooterCookiesUrl)),
      // privacy
      this.store.pipe(select(integrationGetFooterPrivacyUrl)),
      // cookies management
      // document
      this.store.pipe(select(getPartnershipDocument)),
      // accessibility
      this.store.pipe(select(integrationGetFooterAccessibilityUrl)),
    ]).pipe(
      map(([cookiesUrl, privacyUrl, documentUrl, accessibilityUrl]) => {
        const links: FooterLink[] = [
          // cookies
          cookiesUrl
            ? {
                type: 'link',
                href: cookiesUrl,
                lang: 'it',
                target: '_blank',
                label: this.translateService.instant('FOOTER.COOKIES.TEXT'),
              }
            : ({} as FooterLink),
          // privacy
          privacyUrl
            ? {
                type: 'link',
                href: privacyUrl,
                lang: 'it',
                target: '_blank',
                label: this.translateService.instant('FOOTER.PRIVACY.TEXT'),
              }
            : ({} as FooterLink),
          // cookies management
          {
            type: 'cookies_management',
            lang: 'it',
            label: this.translateService.instant('FOOTER.ONETRUST.TEXT'),
          },
          // document
          documentUrl
            ? {
                type: 'text_document',
                href: documentUrl,
                lang: 'it',
                documentTitleLabel: this.translateService.instant('FOOTER.DOCUMENT.TITLE'),
                target: '_blank',
                label: this.translateService.instant('FOOTER.DOCUMENT.TEXT'),
              }
            : ({} as FooterLink),
          // copyright
          {
            type: 'copyright',
            lang: 'it',
            label: this.translateService.instant('FOOTER.COPYRIGHT.TEXT', { year: getDate().getFullYear() }),
          },
          // accessibility
          accessibilityUrl
            ? {
                type: 'link',
                href: accessibilityUrl,
                lang: 'it',
                target: '_blank',
                label: this.translateService.instant('FOOTER.ACCESSIBILITY.TEXT'),
              }
            : ({} as FooterLink),
        ];
        return links;
      })
    );
  }

  ngOnInit(): void {
    this.store.dispatch(GetIntegrationsRequest({}));

    this.partnershipIntegrationInd$ = this.store.pipe(select(getPartnershipIntegrationInd));

    this.partnershipUrl$ = this.store.pipe(select(getPartnershipUrl));
    this.partnershipImage$ = this.store.pipe(select(getPartnershipImage));
    this.store.pipe(select(getPartnershipName), takeUntil(this.destroy$)).subscribe(partnershipName => {
      const partnershipAlt = 'ACCESSIBILITY.HEADER.PROMOTIONAL_BANNER_ALT';
      this.promotionalAlt = this.translateService.instant(partnershipAlt, { partnershipName });
    });

    this.vertiUrl$ = this.store.pipe(select(integrationGetVertiCommercialPageUrl));

    this.store
      .pipe(select(getProjectData))
      .pipe(takeUntil(this.destroy$))
      .subscribe((project: HomeProject) => {
        this.homeProject = project;
        this.homeTypeName = this.homeProject?.homeRisks?.[0]?.homeDetail?.homeTypeDesc;
      });

    this.store
      .pipe(select(integrationGetGenesysChatConfig))
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        this.genesysChatConfig = config;
      });

    this.store
      .pipe(select(getGenesysChatInfo))
      .pipe(takeUntil(this.destroy$))
      .subscribe(genesysChatInfo => {
        this.genesysChatInfo = genesysChatInfo;
      });

    this.checkGenesysPage();

    this.onDesktop = window.innerWidth > 768;
  }

  private checkGenesysPage(): void {
    if (window.isChatOpened) {
      this.chatAvailable = true;
      return;
    }
    if (!this.chatConfigVisibilityService.visibleInd) {
      this.chatAvailable = false;
      return;
    }
    const risk = RISKHOME;
    const chatVisiblePageInd = this.genesysChatInfo?.visiblePages?.[risk]?.[this.flowName]?.find(
      (page: string) => page === this.stepName
    )
      ? true
      : false;

    if (chatVisiblePageInd && this.chatConfigVisibilityService.visibleInd) {
      this.applicationUtilitiesService
        .getChatVisibility({ customerType: 'self', requestType: 'partial' })
        .subscribe(resp => {
          this.chatAvailable = resp.chatVisibilityStatus === 'AVAILABLE';
        });
    }
  }

  openChat(): void {
    this.genesysChatService.openChat();
  }

  replaceHyphensWithSpaces(text: string): string {
    return text.replace(/[-_]/g, ' ');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  goToVerti(): void {
    this.integrationService.goToVerti();
  }
}
